import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2, Users, Loader2, Check, ChevronDown, Eye, MessageCircle, Edit3 } from 'lucide-react';
import { vaultSphereAPI, teamsAPI, type ShareRole } from '../services/api';

export type SharedWithEntry =
  | { type: 'user'; userId: string; name?: string; email?: string; role: ShareRole; sharedAt?: string; sharedBy?: { userId: string; name?: string; email?: string }; sharedViaTeam?: { teamId: string; teamName?: string } }
  | { type: 'team'; teamId: string; teamName?: string; memberCount?: number; role: ShareRole; sharedAt?: string; sharedBy?: { userId: string; name?: string; email?: string }; sharedViaTeam?: { teamId: string; teamName?: string } };

interface VaultSphereShareModalProps {
  item: { id: string; name: string; type: 'file' | 'folder' };
  onClose: () => void;
  onSuccess?: () => void;
}

const ROLE_LABELS: Record<ShareRole, string> = {
  viewer: 'Viewer',
  commenter: 'Commenter',
  editor: 'Editor',
};

const ROLE_DESC: Record<ShareRole, string> = {
  viewer: 'Can only view',
  commenter: 'Can view and comment',
  editor: 'Can view, edit and share',
};

export const VaultSphereShareModal: React.FC<VaultSphereShareModalProps> = ({ item, onClose, onSuccess }) => {
  const [sharedWith, setSharedWith] = useState<SharedWithEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userTeams, setUserTeams] = useState<{ _id: string; name: string }[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [teamMembers, setTeamMembers] = useState<{ _id: string; name: string; email: string }[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [selectedRole, setSelectedRole] = useState<ShareRole>('viewer');
  const [sharing, setSharing] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchSharedWith = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = item.type === 'file'
        ? await vaultSphereAPI.getFileSharedWith(item.id)
        : await vaultSphereAPI.getFolderSharedWith(item.id);
      const raw = (res.data?.data?.sharedWith ?? []) as { type?: string; userId?: string; teamId?: string; name?: string; email?: string; teamName?: string; memberCount?: number; role?: ShareRole; sharedAt?: string; sharedBy?: { userId: string; name?: string; email?: string }; sharedViaTeam?: { teamId: string; teamName?: string } }[];
      const list: SharedWithEntry[] = raw.map((e) => {
        if (e.type === 'team' && e.teamId) {
          return { type: 'team', teamId: e.teamId, teamName: e.teamName, memberCount: e.memberCount, role: (e.role || 'viewer') as ShareRole, sharedAt: e.sharedAt, sharedBy: e.sharedBy, sharedViaTeam: e.sharedViaTeam };
        }
        const uid = e.userId ?? (e as { userId?: string }).userId;
        return { type: 'user', userId: uid ?? '', name: e.name, email: e.email, role: (e.role || 'viewer') as ShareRole, sharedAt: e.sharedAt, sharedBy: e.sharedBy, sharedViaTeam: e.sharedViaTeam };
      });
      setSharedWith(list);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string }; status?: number }; message?: string };
      const msg = typeof ax?.response?.data?.error === 'string' ? ax.response.data.error
        : ax?.response?.status === 404 ? (item.type === 'file' ? 'File not found.' : 'Folder not found.')
        : ax?.message && typeof ax.message === 'string' && ax.message !== 'Network Error' ? ax.message
        : 'Unable to load access.';
      setError(msg);
      setSharedWith([]);
    } finally {
      setLoading(false);
    }
  }, [item.id, item.type]);

  useEffect(() => { fetchSharedWith(); }, [fetchSharedWith]);

  const fetchUserTeams = useCallback(async () => {
    try {
      const res = await teamsAPI.getUserTeams();
      const data = (res.data?.data ?? []) as { _id: string; name: string }[];
      setUserTeams(Array.isArray(data) ? data : []);
    } catch {
      setUserTeams([]);
    }
  }, []);

  useEffect(() => { fetchUserTeams(); }, [fetchUserTeams]);

  useEffect(() => {
    if (!selectedTeamId) {
      setTeamMembers([]);
      setSelectedMemberIds(new Set());
      return;
    }
    teamsAPI.getTeamById(selectedTeamId)
      .then((res) => {
        const team = res.data?.data;
        const members = (team?.members ?? []) as { _id: string; name: string; email: string }[];
        setTeamMembers(members.map((m: { _id?: string; id?: string }) => ({ _id: m._id ?? (m as { id?: string }).id ?? '', name: (m as { name?: string }).name ?? '', email: (m as { email?: string }).email ?? '' })));
        setSelectedMemberIds(new Set());
      })
      .catch(() => setTeamMembers([]));
  }, [selectedTeamId]);

  const toggleMember = (id: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllMembers = () => {
    if (selectedMemberIds.size === teamMembers.length) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(teamMembers.map((m) => m._id)));
    }
  };

  const handleShare = async () => {
    if (selectedMemberIds.size === 0) {
      setError('Select at least one member.');
      return;
    }
    setSharing(true);
    setError(null);
    try {
      const sharedViaTeamId = selectedTeamId || undefined;
      const memberIds = Array.from(selectedMemberIds);
      for (const uid of memberIds) {
        if (item.type === 'file') {
          await vaultSphereAPI.shareFile(item.id, { userId: uid, role: selectedRole, sharedViaTeamId });
        } else {
          await vaultSphereAPI.shareFolder(item.id, { userId: uid, role: selectedRole, sharedViaTeamId });
        }
      }
      await fetchSharedWith();
      setSelectedTeamId('');
      setSelectedMemberIds(new Set());
      setTeamMembers([]);
      onSuccess?.();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Impossible de partager.';
      setError(msg);
    } finally {
      setSharing(false);
    }
  };

  const handleRemove = async (entry: SharedWithEntry) => {
    const id = entry.type === 'user' ? entry.userId : entry.teamId;
    setRemovingId(id);
    setError(null);
    try {
      if (entry.type === 'user') {
        if (item.type === 'file') await vaultSphereAPI.removeFileShare(item.id, entry.userId);
        else await vaultSphereAPI.removeFolderShare(item.id, entry.userId);
      } else {
        if (item.type === 'file') await vaultSphereAPI.removeFileShareTeam(item.id, entry.teamId);
        else await vaultSphereAPI.removeFolderShareTeam(item.id, entry.teamId);
      }
      await fetchSharedWith();
      onSuccess?.();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
        ? (err as { response: { data: { error: string } } }).response.data.error
        : 'Unable to remove access.';
      setError(msg);
    } finally {
      setRemovingId(null);
    }
  };

  const getInitials = (name?: string, email?: string) => {
    if (name && name.trim()) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    }
    if (email) return email.slice(0, 2).toUpperCase();
    return '?';
  };

  const alreadyUserIds = new Set(sharedWith.filter((e): e is SharedWithEntry & { type: 'user' } => e.type === 'user').map((e) => e.userId));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Share &quot;{item.name}&quot;</h2>
              <p className="text-xs text-slate-500">{item.type === 'folder' ? 'Dossier' : 'Fichier'}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          {/* Sélection équipe + membres */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Share with a team</label>
            <div className="relative">
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 bg-white"
              >
                <option value="">Choose a team</option>
                {userTeams.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>

            {selectedTeamId && teamMembers.length > 0 && (
              <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-600">Select members</span>
                  <button type="button" onClick={selectAllMembers} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                    {selectedMemberIds.size === teamMembers.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as ShareRole)}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 bg-white"
                  >
                    {(['viewer', 'commenter', 'editor'] as ShareRole[]).map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </div>
                <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                  {teamMembers.map((m) => {
                    const isAlreadyShared = alreadyUserIds.has(m._id);
                    return (
                      <li key={m._id} className={`flex items-center gap-2 p-2 rounded-lg ${isAlreadyShared ? 'bg-amber-50 opacity-75' : 'hover:bg-white'}`}>
                        <button
                          type="button"
                          onClick={() => !isAlreadyShared && toggleMember(m._id)}
                          disabled={isAlreadyShared}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isAlreadyShared ? 'border-amber-300 bg-amber-100' : selectedMemberIds.has(m._id) ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'
                          }`}
                        >
                          {selectedMemberIds.has(m._id) && <Check className="w-3 h-3" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{m.name || m.email}</p>
                          {m.email && <p className="text-xs text-slate-500 truncate">{m.email}</p>}
                        </div>
                        {isAlreadyShared && <span className="text-xs text-amber-600">Already shared</span>}
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  disabled={sharing || selectedMemberIds.size === 0}
                  onClick={handleShare}
                  className="w-full mt-3 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  Share with {selectedMemberIds.size} member{selectedMemberIds.size > 1 ? 's' : ''}
                </button>
              </div>
            )}

            {selectedTeamId && teamMembers.length === 0 && (
              <p className="mt-2 text-xs text-slate-500">No members in this team.</p>
            )}
          </div>

          {/* Current access */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Current access</h3>
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
              </div>
            ) : sharedWith.length === 0 ? (
              <div className="py-6 text-center text-slate-500 text-sm rounded-xl bg-slate-50 border border-dashed border-slate-200">
                No shares yet. Select a team and members above.
              </div>
            ) : (
              <ul className="space-y-2">
                {sharedWith.map((entry) => (
                  <li key={entry.type === 'user' ? entry.userId : entry.teamId} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {entry.type === 'user' ? getInitials(entry.name, entry.email) : <Users className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {entry.type === 'user' ? (entry.name || entry.email || entry.userId) : (entry.teamName || entry.teamId)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {entry.type === 'user' ? entry.email : `${entry.memberCount ?? 0} membre(s)`} · {ROLE_LABELS[entry.role]}
                        </p>
                        {(entry.sharedBy || entry.sharedViaTeam) && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {entry.sharedBy?.name && <>Shared by {entry.sharedBy.name}</>}
                            {entry.sharedBy?.name && entry.sharedViaTeam?.teamName && ' · '}
                            {entry.sharedViaTeam?.teamName && <>Team {entry.sharedViaTeam.teamName}</>}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={removingId === (entry.type === 'user' ? entry.userId : entry.teamId)}
                      onClick={() => handleRemove(entry)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Remove access"
                    >
                      {removingId === (entry.type === 'user' ? entry.userId : entry.teamId) ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
            <p className="text-xs font-medium text-slate-600 mb-2">Access levels</p>
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li className="flex items-center gap-2"><Eye className="w-3.5 h-3.5 text-slate-400" /> <strong>Viewer</strong> — {ROLE_DESC.viewer}</li>
              <li className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-slate-400" /> <strong>Commenter</strong> — {ROLE_DESC.commenter}</li>
              <li className="flex items-center gap-2"><Edit3 className="w-3.5 h-3.5 text-slate-400" /> <strong>Editor</strong> — {ROLE_DESC.editor}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultSphereShareModal;
